import { useMemo } from 'react'

interface Event {
  team: 'team1' | 'team2'
  points: number
  timestamp: number
  type?: 'game' | 'set'
  key?: string
}

export interface PointData {
  point: number
  cumulative: number
  color: string
  timestamp: number
  setIndex?: number // Index of the set this point belongs to (0-based)
  isSetBoundary?: boolean
  isGameBoundary?: boolean
  isLastPointInSet?: boolean
  isLastPointInGame?: boolean
}

const VISUAL_OFFSET = 0.1

export function useProcessedPointData(events: Record<string, Event> | undefined, team1Color: string, team2Color: string): PointData[] {
  return useMemo(() => {
    if (!events) return []

    const entries = Object.entries(events)
      .filter(([key]) => key !== 'lastReverseAt')
      .map(([key, event]) => ({ ...event, key }))
      .filter((event, index, self) =>
        index === self.findIndex(e => e.key === event.key)
      )
      .sort((a, b) => a.timestamp - b.timestamp)

    let cumulative = 0
    let currentSetIndex = 0
    const pointData: PointData[] = []

    entries.forEach((event) => {
      if (event.points === -1) return
      cumulative += event.team === 'team1' ? 1 : -1

      pointData.push({
        point: pointData.length + 1,
        cumulative: cumulative === 0
          ? (event.team === 'team1' ? VISUAL_OFFSET : -VISUAL_OFFSET)
          : cumulative,
        color: event.team === 'team1' ? team1Color : team2Color,
        timestamp: event.timestamp,
        setIndex: currentSetIndex,
      })
      
      // Increment set index when we encounter a set boundary
      if (event.type === 'set') {
        currentSetIndex++
      }
    })

    // Mark only the true last point in each set/game
    const markTrueLastPoints = (type: 'game' | 'set', key: 'isLastPointInGame' | 'isLastPointInSet') => {
      // Find all boundary events of the given type
      const boundaryEvents = entries.filter(e => e.type === type)
      
      // For each boundary event, find the exact point that corresponds to it
      for (const boundaryEvent of boundaryEvents) {
        // Find the pointData entry that has the exact same timestamp as this boundary event
        const matchingPoint = pointData.find(point => point.timestamp === boundaryEvent.timestamp)
        if (matchingPoint) {
          matchingPoint[key] = true
        }
      }
    }

    markTrueLastPoints('set', 'isLastPointInSet')
    markTrueLastPoints('game', 'isLastPointInGame')

    return pointData
  }, [events, team1Color, team2Color])
} 